import * as _ from 'underscore';

import { Messages } from '../../codegen/Localize';
import { FeatureFlags } from '../FeatureFlags';
import { ListResult } from '../fetchers/ListResult';
import { ActionContext } from '../helpers/ActionContext';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { IRemoteRefreshAgentInfo } from '../models/IRemoteRefreshAgentInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { OAuthCredentials } from '../resources/OAuthCredentials';
import { ServerUsers } from '../resources/ServerUsers';
import { IConnectedDevice } from '../ServerApi/types/IConnectedDevice';
import { IOAuthCredential, IOAuthCredentialTypeInfo } from '../ServerApi/types/IOAuthCredential';
import { AddOAuthCredentialAction } from '../services/AddOAuthCredentialAction';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from '../services/ConfirmActionDialog';
import { DeleteConnectedDeviceAction } from '../services/DeleteConnectedDeviceAction';
import { DeleteMyCredentialsAction } from '../services/DeleteMyCredentialsAction';
import { DeleteOAuthCredentialsAction } from '../services/DeleteOAuthCredentialsAction';
import { IOAuthCredentialsService } from '../services/OAuthCredentialsService';
import { PersonalAccessTokensService } from '../services/PersonalAccessTokensService';
import { ServerService } from '../services/ServerService';
import { DefaultAuthenticationProviderId } from '../services/SiteAuthenticationSettingsService';
import { UserSettingsCtrlCommon } from '../services/UserSettingsCtrlCommon';


export class SiteUserSettingsSubspaceCtrl {

  public static $inject = [
    '$scope',
    '$translate',
    'UserSettingsCtrlCommon',

    // Local Services
    'OAuthCredentials',
    'AddOAuthCredentialAction',
    'ConfirmActionDialog',
    'DeleteOAuthCredentialsAction',
    'DeleteMyCredentialsAction',
    'ServerUsers',
    'DeleteConnectedDeviceAction',
    'PersonalAccessTokensService',
    'OAuthCredentialsService',
    'ServerService'
  ];

  constructor(
    private $scope: SiteUserSettingsSubspaceCtrl.Scope,
            $translate: ng.translate.ITranslateService,
            userSettingsCtrlCommon: UserSettingsCtrlCommon,
    private oAuthCredentials: OAuthCredentials,
            addOAuthCredentialAction: AddOAuthCredentialAction,
            ConfirmActionDialog: ConfirmActionDialog,
            deleteOAuthCredentialsAction: DeleteOAuthCredentialsAction,
            deleteMyCredentialsAction: DeleteMyCredentialsAction,
            serverUsers: ServerUsers,
            deleteConnectedDeviceAction: DeleteConnectedDeviceAction,
            personalAccessTokensService: PersonalAccessTokensService,
    private oauthCredentialsService: IOAuthCredentialsService,
    private server: ServerService,
  ) {

    // These come from the parent AngularDirective in SiteUserSettingsSubspace.tsx
    const isSessionUserSelf = $scope.isSessionUserSelf;
    const userSettings = $scope.userSettings;
    const userRefreshTokenCount = $scope.userRefreshTokenCount;
    const serverUser = $scope.serverUser;
    const siteUser = $scope.siteUser;

    this.failureNotificationContent();
    userSettingsCtrlCommon.initScope($scope, isSessionUserSelf, serverUser, siteUser, userSettings, userRefreshTokenCount);

    // TODO: Replace with a session variable check.  See the fix from http://fogbugz/default.asp?142850 for more details
    $scope.canManageCredentials = true;

    $scope.isSAMLUser = !!siteUser && !!siteUser.authenticationProviderId && siteUser.authenticationProviderId !== DefaultAuthenticationProviderId;

    // Connected devices

    const refreshTokensCallback = personalAccessTokensService.loadTokensIntoScope(<any>$scope, serverUser);

    const refreshConnectedDevices = () => serverUsers.getUserConnectedDevices(serverUser.id)
      .then((devices:IConnectedDevice[]) => $scope.connectedDevices = devices).then(refreshTokensCallback);

    $scope.deleteConnectedDevice = (device:IConnectedDevice, $event:JQueryEventObject) =>
      deleteConnectedDeviceAction.execute(device, serverUser.id, ActionContext.fromEvent($event))
        .then(refreshConnectedDevices);

    personalAccessTokensService.loadActionsIntoScope(<any>$scope, serverUser, isSessionUserSelf, refreshConnectedDevices);

    refreshConnectedDevices();



    // Credentials list

    // TODO: Filter out invalid credentials, needs server support
    $scope.credentialsLists = this.createOAuthCredentialLists();

    const refreshCredentials = () => {
      SliceableHelper.fetchAll(oAuthCredentials.all())
        .then<void>((credentialsListResult: ListResult<IOAuthCredential>) => {
          $scope.credentialsLists = this.createOAuthCredentialLists(credentialsListResult.items);
        });
    };

    refreshCredentials();

    // Initialize credential actions
    const getOauthServerAndRedirect = (typeInfo: IOAuthCredentialTypeInfo) => {
        const displayscope = <SiteUserSettingsSubspaceCtrl.OauthUrlScope>$scope.$new();
        displayscope.description = $translate.instant('addServerName');
        displayscope.input = {
            text: '',
            maxLength: 200
        };
        const dialogInstance = ConfirmActionDialog.open({
            title: $translate.instant('addServerNameTitle'),
            bodyTemplateUrl: 'confirmActionDialogInput.html',
            scope: displayscope,
            position: { reserveHeight: 250 },
            confirmDisabled: () => _.isEmpty(displayscope.input.text),
            actionName:$translate.instant('Add'),
            action: (dialogInstance: IConfirmActionDialogInstance) => {
                addOAuthCredentialAction.attemptAuth(typeInfo.type, 'https://' + displayscope.input.text).then(refreshCredentials);
            }
        });
    };
    $scope.deleteAllCredentials = ($event: JQueryEventObject) =>
      deleteMyCredentialsAction.executeFromEvent($event).then(refreshCredentials);

    $scope.addCredentialHref = (typeInfo: IOAuthCredentialTypeInfo) => addOAuthCredentialAction.href(typeInfo);
    $scope.addCredential = (typeInfo: IOAuthCredentialTypeInfo) => {
       //TFS834312 - For Snowflake OAuth, we need InstanceUrl to redirect to Oauth Page
       if (typeInfo.type === 'snowflake') {
            getOauthServerAndRedirect(typeInfo);
        }
        else {
            addOAuthCredentialAction.attemptAuth(typeInfo.type).then(refreshCredentials);
        }
    };

    $scope.deleteCredential = (oauthCredential: SiteUserSettingsSubspaceCtrl.OAuthCredentialViewModel, $event: JQueryEventObject) =>
      deleteOAuthCredentialsAction.executeFromEvent(Set.fromIdItemArray([oauthCredential.credential]), $event)
        .then(refreshCredentials);

    $scope.testCredential = (oauthCredential: SiteUserSettingsSubspaceCtrl.OAuthCredentialViewModel) => this.testCredential(oauthCredential);

    $scope.$on('$destroy', () => addOAuthCredentialAction.cancelPendingAuthAttempt());
  }

  public failureNotificationContent() {
    const showFailureNotifForEAR: boolean = this.server.isExtractEncryptionEnabledOmniSwitch();
    if(showFailureNotifForEAR && this.server.flowsOnSiteEnabled()) {
      this.$scope.failureNotificationTitle = Messages.refreshOrFlowOrEncryptionRunFailureEmailNotificationSettingSection();
      this.$scope.failureNotificationDetail = Messages.refreshOrFlowOrEncryptionFailureEmailNotificationUserSettingDetail();
      this.$scope.failureNotificationCheckboxLabel = Messages.refreshOrFlowOrEncryptionFailureEmailNotificationUserSettingEnable();
    } else if (showFailureNotifForEAR && !this.server.flowsOnSiteEnabled()) {
      this.$scope.failureNotificationTitle = Messages.refreshOrEncryptionRunFailureEmailNotificationSettingSection();
      this.$scope.failureNotificationDetail = Messages.refreshOrEncryptionFailureEmailNotificationUserSettingDetail();
      this.$scope.failureNotificationCheckboxLabel = Messages.refreshOrEncryptionFailureEmailNotificationUserSettingEnable();
    } else if (!showFailureNotifForEAR && this.server.flowsOnSiteEnabled()) {
      this.$scope.failureNotificationTitle = Messages.refreshOrFlowRunFailureEmailNotificationSettingSection();
      this.$scope.failureNotificationDetail = Messages.refreshOrFlowFailureEmailNotificationUserSettingDetail();
      this.$scope.failureNotificationCheckboxLabel = Messages.refreshOrFlowFailureEmailNotificationUserSettingEnable();
    } else {
      this.$scope.failureNotificationTitle = Messages.refreshFailureEmailNotificationSettingSection();
      this.$scope.failureNotificationDetail = Messages.refreshFailureEmailNotificationUserSettingDetail();
      this.$scope.failureNotificationCheckboxLabel = Messages.refreshFailureEmailNotificationUserSettingEnable();
    }
  }

  private testCredential(oauthCredential: SiteUserSettingsSubspaceCtrl.OAuthCredentialViewModel) {
    oauthCredential.test = { inProgress: true };

    const handleTestResult = (success: any) => {
      oauthCredential.test = {
        success: !!success
      };
    };

    this.oAuthCredentials.test(oauthCredential.credential.id)
      .then(handleTestResult, () => handleTestResult(false));
  }

  private createOAuthCredentialLists(credentials?:IOAuthCredential[]):SiteUserSettingsSubspaceCtrl.OAuthCredentialsList[] {
    const convertToCredentialViewModel = (credential:IOAuthCredential):SiteUserSettingsSubspaceCtrl.OAuthCredentialViewModel => {
      return {credential: credential, test: { enabled: this.oauthCredentialsService.supportsTestConnection(credential.type) }};
    };

    const credentialVMGroupsMap = _.groupBy(credentials, c => c.type);

    // Iterate through all OAuth types, if any is enabled, put it in  the result
    return _.compact(_.map(this.oauthCredentialsService.getAllOAuthCredentialTypes(),
      (typeInfo:IOAuthCredentialTypeInfo) => {
        const oauthCredentialsList:SiteUserSettingsSubspaceCtrl.OAuthCredentialsList = {
          typeInfo: typeInfo,
          addEnabled: this.oAuthCredentials.canAddForConnectionType(typeInfo),
          credentials: _.map(credentialVMGroupsMap[typeInfo.type], convertToCredentialViewModel)
        };

        return (oauthCredentialsList.addEnabled || oauthCredentialsList.credentials.length > 0) ? oauthCredentialsList : null;
      })
    );
  }
}

export module SiteUserSettingsSubspaceCtrl {

  export interface OAuthCredentialViewModel  {
    credential: IOAuthCredential;
    test: {
      inProgress?: boolean;
      success?: boolean;
      // Determine if test button is displayed to customers, as of Q4 2016, we disable them for
      // generic-auth-based WDC credentials. TFS 546010
      enabled?: boolean;
    };
  }

  export interface OAuthCredentialsList {
    typeInfo: IOAuthCredentialTypeInfo;
    addEnabled: boolean;
    credentials?: OAuthCredentialViewModel[];
  }

  export interface RemoteRefreshAgentsScope extends ng.IScope {
    deleteRemoteRefreshAgent(agent: IRemoteRefreshAgentInfo, $event: JQueryEventObject): void;
    remoteRefreshAgents: IRemoteRefreshAgentInfo[];
  }

  export interface OauthUrlScope extends ng.IScope {
      description: string;
      input: {
          text: string;
          maxLength?: number;
      };
  }

  export interface Scope extends UserSettingsCtrlCommon.Scope, RemoteRefreshAgentsScope {
    // Credentials

    canManageCredentials: boolean;

    deleteAllCredentials($event: JQueryEventObject): void;

    addCredential(typeInfo: IOAuthCredentialTypeInfo): void;
    addCredentialHref(typeInfo: IOAuthCredentialTypeInfo): string;

    testCredential(oauthCredential: OAuthCredentialViewModel): any;
    deleteCredential(oauthCredential: OAuthCredentialViewModel, $event: JQueryEventObject): any;

    credentialsLists: OAuthCredentialsList[];

    objectName: string;

    isSAMLUser: boolean;

    remoteAgentManagerEnabled: boolean;

    connectedDevices: IConnectedDevice[];
    deleteConnectedDevice(token: IConnectedDevice, $event: JQueryEventObject): void;
    failureNotificationTitle: string;
    failureNotificationDetail: string;
    failureNotificationCheckboxLabel: string;

    userRefreshTokenCount: number;
    isSessionUserSelf: boolean;
  }
}

Module.instance().controller('SiteUserSettingsSubspaceCtrl', SiteUserSettingsSubspaceCtrl);
